import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Space, Drawer, Grid, Badge, List, Empty, Typography } from 'antd';
import {
  HomeOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuOutlined,
  CarOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  WalletOutlined,
  MessageOutlined,
  BellOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setLanguage, SUPPORTED } from '../i18n';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';

const { useBreakpoint } = Grid;
const { Header: AntHeader } = Layout;

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation('common');
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifList, setNotifList] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const screens = useBreakpoint();

  // 未读消息角标：合并订单消息 + 会话两路未读数，轮询 + 路由变化时刷新
  useEffect(() => {
    if (!user) {
      setUnread(0);
      return undefined;
    }
    let active = true;
    const fetchUnread = async () => {
      try {
        const [m, c] = await Promise.all([
          api.get('/messages/unread-count'),
          api.get('/conversations/unread-count'),
        ]);
        if (active) setUnread((m.data.count || 0) + (c.data.count || 0));
      } catch (e) {
        // 静默：角标失败不打扰用户
      }
    };
    fetchUnread();
    // 标签页在后台时不轮询（省后端通用限流额度），切回来后下一拍即刷新
    const timer = setInterval(() => { if (!document.hidden) fetchUnread(); }, 15000);
    return () => { active = false; clearInterval(timer); };
  }, [user, location.pathname]);

  // 通知未读角标：轮询 + 路由变化时刷新
  useEffect(() => {
    if (!user) {
      setNotifUnread(0);
      return undefined;
    }
    let active = true;
    const fetchNotifUnread = async () => {
      try {
        const r = await api.get('/notifications/unread-count');
        if (active) setNotifUnread(r.data.count || 0);
      } catch (e) {
        // 静默
      }
    };
    fetchNotifUnread();
    const timer = setInterval(() => { if (!document.hidden) fetchNotifUnread(); }, 20000);
    return () => { active = false; clearInterval(timer); };
  }, [user, location.pathname]);

  const fetchNotifList = useCallback(async () => {
    try {
      const r = await api.get('/notifications', { params: { limit: 10 } });
      setNotifList(r.data.notifications || []);
    } catch (e) {
      // 静默
    }
  }, []);

  const handleNotifOpenChange = (open) => {
    setNotifOpen(open);
    if (open) fetchNotifList();
  };

  const handleNotifClick = async (n) => {
    setNotifOpen(false);
    if (!n.is_read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setNotifUnread((c) => Math.max(0, c - 1));
        setNotifList((list) => list.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      } catch (e) {
        // 静默：标记失败不阻断跳转
      }
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifUnread(0);
      setNotifList((list) => list.map((x) => ({ ...x, is_read: true })));
    } catch (e) {
      // 静默
    }
  };

  const notifDropdown = (
    <div style={{ width: 340, background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <span style={{ fontWeight: 600 }}>{t('notifications.title')}</span>
        {notifUnread > 0 && (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={handleMarkAllRead}>{t('notifications.markAllRead')}</Button>
        )}
      </div>
      {notifList.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('notifications.empty')} style={{ padding: '24px 0' }} />
      ) : (
        <List
          size="small"
          dataSource={notifList}
          style={{ maxHeight: 360, overflowY: 'auto' }}
          renderItem={(n) => (
            <List.Item
              onClick={() => handleNotifClick(n)}
              style={{ cursor: 'pointer', padding: '10px 16px', background: n.is_read ? '#fff' : '#f0f7ff' }}
            >
              <div style={{ width: '100%' }}>
                <div style={{ fontWeight: n.is_read ? 400 : 600, fontSize: 13 }}>{n.title}</div>
                {n.body && (
                  <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ margin: '2px 0 0', fontSize: 12, color: '#888' }}>
                    {n.body}
                  </Typography.Paragraph>
                )}
              </div>
            </List.Item>
          )}
        />
      )}
    </div>
  );

  const handleMenuClick = ({ key }) => {
    navigate(key);
    setMobileMenuVisible(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const userMenuItems = user?.role === 'admin' ? [
    {
      key: '/admin/dashboard',
      icon: <HomeOutlined />,
      label: t('nav.adminDashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('nav.logout'),
      onClick: handleLogout,
    },
  ] : [
    {
      key: '/dashboard',
      icon: <HomeOutlined />,
      label: t('nav.dashboard'),
    },
    {
      key: '/my/listings',
      icon: <CarOutlined />,
      label: t('nav.myListings'),
    },
    {
      key: '/profile',
      icon: <UserOutlined />,
      label: t('nav.profile'),
    },
    {
      key: '/verification',
      icon: <UserOutlined />,
      label: t('nav.verification'),
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: t('nav.settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('nav.logout'),
      onClick: handleLogout,
    },
  ];

  const publicMenuItems = [
    {
      key: '/',
      label: t('nav.home'),
    },
    {
      key: '/trips',
      label: t('nav.trips'),
    },
    {
      key: '/requests',
      label: t('nav.requests'),
    },
  ];

  const authenticatedMenuItems = [
    {
      key: '/',
      label: t('nav.home'),
    },
    {
      key: '/trips',
      icon: <CarOutlined />,
      label: t('nav.trips'),
    },
    {
      key: '/requests',
      icon: <ShoppingCartOutlined />,
      label: t('nav.requests'),
    },
    {
      key: '/orders',
      icon: <FileTextOutlined />,
      label: t('nav.orders'),
    },
    {
      key: '/messages',
      icon: <MessageOutlined />,
      label: unread > 0 ? <Badge count={unread} size="small" offset={[10, -2]}>{t('nav.messages')}</Badge> : t('nav.messages'),
    },
    {
      key: '/wallet',
      icon: <WalletOutlined />,
      label: t('nav.wallet'),
    },
  ];

  // 界面语言切换（未登录也可用；登录用户在「设置」里切换会同时保存到账号）
  const LANG_LABEL_KEY = { 'zh-CN': 'language.zhCN', en: 'language.en', 'zh-TW': 'language.zhTW' };
  const languageMenu = {
    items: SUPPORTED.map((lng) => ({ key: lng, label: t(LANG_LABEL_KEY[lng]) })),
    selectable: true,
    selectedKeys: [i18n.language],
    onClick: ({ key }) => setLanguage(key),
  };
  const languageSwitch = (
    <Dropdown menu={languageMenu} placement="bottomRight" trigger={['click']}>
      <Button type="text" icon={<GlobalOutlined />} aria-label={t('language.label')}>
        {t(LANG_LABEL_KEY[i18n.language] || 'language.zhCN')}
      </Button>
    </Dropdown>
  );

  const menuItems = user ? authenticatedMenuItems : publicMenuItems;

  return (
    <AntHeader style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 24px'
    }}>
      {/* Logo */}
      <div
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#1890ff',
          cursor: 'pointer'
        }}
        onClick={() => navigate('/')}
      >
        iDelivery
      </div>

      {/* 桌面端菜单 */}
      <div style={{ display: screens.md ? 'block' : 'none' }}>
        <Menu
          mode="horizontal"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            border: 'none',
            background: 'transparent',
            minWidth: '400px'
          }}
        />
      </div>

      {/* 用户操作区 */}
      <div style={{ display: screens.md ? 'flex' : 'none', alignItems: 'center', gap: '16px' }}>
        {languageSwitch}
        {user ? (
          <Space size="large">
            <Dropdown
              open={notifOpen}
              onOpenChange={handleNotifOpenChange}
              dropdownRender={() => notifDropdown}
              placement="bottomRight"
              trigger={['click']}
            >
              <Badge count={notifUnread} size="small">
                <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />} />
              </Badge>
            </Dropdown>
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleMenuClick }}
              placement="bottomRight"
              arrow
            >
              <Button type="text" style={{ padding: 0, height: 'auto' }}>
                <Space>
                  <Avatar
                    src={user.avatar_url}
                    icon={<UserOutlined />}
                    size="small"
                  />
                  <span>{user.real_name || user.username}</span>
                </Space>
              </Button>
            </Dropdown>
          </Space>
        ) : (
          <Space>
            <Button onClick={() => navigate('/login')}>
              {t('nav.login')}
            </Button>
            <Button
              type="primary"
              onClick={() => navigate('/register')}
            >
              {t('nav.register')}
            </Button>
          </Space>
        )}
      </div>

      {/* 移动端菜单按钮 */}
      <div style={{ display: screens.md ? 'none' : 'block' }}>
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setMobileMenuVisible(true)}
        />
      </div>

      {/* 移动端抽屉菜单 */}
      <Drawer
        title="iDelivery"
        placement="right"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={280}
      >
        <Menu
          mode="vertical"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ border: 'none' }}
        />
        <div style={{ padding: '8px 16px' }}>{languageSwitch}</div>

        {user ? (
          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #f0f0f0' }}>
            <div style={{ marginBottom: '16px', padding: '0 16px' }}>
              <Space>
                <Avatar
                  src={user.avatar_url}
                  icon={<UserOutlined />}
                />
                <div>
                  <div style={{ fontWeight: 'bold' }}>
                    {user.real_name || user.username}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {user.email}
                  </div>
                </div>
              </Space>
            </div>
            <Menu
              mode="vertical"
              items={userMenuItems}
              onClick={handleMenuClick}
              style={{ border: 'none' }}
            />
          </div>
        ) : (
          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #f0f0f0' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                block
                onClick={() => {
                  navigate('/login');
                  setMobileMenuVisible(false);
                }}
              >
                {t('nav.login')}
              </Button>
              <Button
                type="primary"
                block
                onClick={() => {
                  navigate('/register');
                  setMobileMenuVisible(false);
                }}
              >
                {t('nav.register')}
              </Button>
            </Space>
          </div>
        )}
      </Drawer>
    </AntHeader>
  );
};

export default Header;
